import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { US_STATES } from '@/constants/usStates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const emptyDriverLicenseForm = {
  driver_license_number: '',
  dl_issuing_county: '',
  dl_issuing_state: '',
  dl_expiry_date: '',
  date_of_birth: '',
};

const emptyCreditAppForm = {
  landlord_name: '',
  landlord_mobile: '',
  previous_street_address: '',
  previous_residence_years: '',
  previous_residence_months: '',
  previous_city: '',
  previous_state: '',
  previous_zip: '',
  previous_rent_mortgage: '',
  previous_employer: '',
  previous_job_years: '',
  previous_job_months: '',
};

const emptyOtherIncomeForm = {
  other_income_amount: '',
  other_income_frequency: '',
  source_of_other_income: '',
  reference: '',
  relationship: '',
  phone_number: '',
  address: '',
  bank_reference: '',
  bank_phone_number: '',
  bank_account_type: '',
};

const DEFAULT_OTHER_INCOME_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const SECTION_TITLES = {
  landlord: 'Current Landlord Information',
  previous_residential: 'Previous Residential Information',
  previous_employment: 'Previous Employment Information',
};

const inputClass = "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50";
const labelClass = "block text-xs font-['Manrope'] text-white/60 mb-1.5";

export default function CustomerInfoRequestPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestMeta, setRequestMeta] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [dlForm, setDlForm] = useState(emptyDriverLicenseForm);
  const [creditForm, setCreditForm] = useState(emptyCreditAppForm);
  const [otherIncomeForm, setOtherIncomeForm] = useState(emptyOtherIncomeForm);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/customer-info/request/${token}`)
      .then((res) => {
        setRequestMeta(res.data);
        if (res.data.info_type === 'driver_license') {
          const existing = res.data.driver_license_info;
          if (existing) {
            setDlForm({
              driver_license_number: existing.driver_license_number || '',
              dl_issuing_county: existing.dl_issuing_county || '',
              dl_issuing_state: existing.dl_issuing_state || '',
              dl_expiry_date: existing.dl_expiry_date || '',
              date_of_birth: existing.date_of_birth || '',
            });
          }
        }
        if (res.data.info_type === 'credit_app_supplement') {
          const existing = res.data.credit_app_supplement || {};
          const landlord = existing.landlord || {};
          const prevRes = existing.previous_residential || {};
          const prevEmp = existing.previous_employment || {};
          setCreditForm({
            landlord_name: landlord.landlord_name || '',
            landlord_mobile: landlord.landlord_mobile || '',
            previous_street_address: prevRes.previous_street_address || '',
            previous_residence_years: prevRes.previous_residence_years || '',
            previous_residence_months: prevRes.previous_residence_months || '',
            previous_city: prevRes.previous_city || '',
            previous_state: prevRes.previous_state || '',
            previous_zip: prevRes.previous_zip || '',
            previous_rent_mortgage: prevRes.previous_rent_mortgage || '',
            previous_employer: prevEmp.previous_employer || '',
            previous_job_years: prevEmp.previous_job_years || '',
            previous_job_months: prevEmp.previous_job_months || '',
          });
        }
        if (res.data.info_type === 'other_income') {
          const existing = res.data.other_income_info || {};
          setOtherIncomeForm({
            other_income_amount: existing.other_income_amount || '',
            other_income_frequency: existing.other_income_frequency || '',
            source_of_other_income: existing.source_of_other_income || '',
            reference: existing.reference || '',
            relationship: existing.relationship || '',
            phone_number: existing.phone_number || '',
            address: existing.address || '',
            bank_reference: existing.bank_reference || '',
            bank_phone_number: existing.bank_phone_number || '',
            bank_account_type: existing.bank_account_type || '',
          });
        }
        if (res.data.status === 'completed') {
          setSubmitted(true);
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'This request link is invalid or expired');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const buildCreditPayload = () => {
    const sections = requestMeta?.requested_sections || [];
    const payload = {};
    if (sections.includes('landlord')) {
      payload.landlord = {
        landlord_name: creditForm.landlord_name.trim(),
        landlord_mobile: creditForm.landlord_mobile.trim(),
      };
    }
    if (sections.includes('previous_residential')) {
      payload.previous_residential = {
        previous_street_address: creditForm.previous_street_address.trim(),
        previous_residence_years: creditForm.previous_residence_years.trim(),
        previous_residence_months: creditForm.previous_residence_months.trim(),
        previous_city: creditForm.previous_city.trim(),
        previous_state: creditForm.previous_state.trim(),
        previous_zip: creditForm.previous_zip.trim(),
        previous_rent_mortgage: creditForm.previous_rent_mortgage.trim(),
      };
    }
    if (sections.includes('previous_employment')) {
      payload.previous_employment = {
        previous_employer: creditForm.previous_employer.trim(),
        previous_job_years: creditForm.previous_job_years.trim(),
        previous_job_months: creditForm.previous_job_months.trim(),
      };
    }
    return payload;
  };

  const validateOtherIncomeForm = () => {
    const required = [
      otherIncomeForm.other_income_amount,
      otherIncomeForm.other_income_frequency,
      otherIncomeForm.source_of_other_income,
      otherIncomeForm.reference,
      otherIncomeForm.relationship,
      otherIncomeForm.phone_number,
      otherIncomeForm.address,
      otherIncomeForm.bank_reference,
      otherIncomeForm.bank_phone_number,
    ];
    if (required.some((v) => !String(v).trim())) {
      return 'Please complete all required fields';
    }
    if (!otherIncomeForm.bank_account_type) {
      return 'Please select Checking or Saving';
    }
    return null;
  };

  const validateCreditForm = () => {
    const sections = requestMeta?.requested_sections || [];
    if (sections.includes('landlord') && (!creditForm.landlord_name.trim() || !creditForm.landlord_mobile.trim())) {
      return 'Please complete all landlord fields';
    }
    if (sections.includes('previous_residential')) {
      const required = [
        creditForm.previous_street_address,
        creditForm.previous_residence_years,
        creditForm.previous_residence_months,
        creditForm.previous_city,
        creditForm.previous_state,
        creditForm.previous_zip,
        creditForm.previous_rent_mortgage,
      ];
      if (required.some((v) => !String(v).trim())) {
        return 'Please complete all previous residential fields';
      }
    }
    if (sections.includes('previous_employment')) {
      if (!creditForm.previous_employer.trim() || !creditForm.previous_job_years.trim() || !creditForm.previous_job_months.trim()) {
        return 'Please complete all previous employment fields';
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let payload;
    if (requestMeta?.info_type === 'driver_license') {
      if (!dlForm.driver_license_number || !dlForm.dl_issuing_county || !dlForm.dl_issuing_state
        || !dlForm.dl_expiry_date || !dlForm.date_of_birth) {
        toast.error('Please complete all required fields');
        return;
      }
      payload = dlForm;
    } else if (requestMeta?.info_type === 'other_income') {
      const err = validateOtherIncomeForm();
      if (err) {
        toast.error(err);
        return;
      }
      payload = {
        other_income_amount: otherIncomeForm.other_income_amount.trim(),
        other_income_frequency: otherIncomeForm.other_income_frequency,
        source_of_other_income: otherIncomeForm.source_of_other_income.trim(),
        reference: otherIncomeForm.reference.trim(),
        relationship: otherIncomeForm.relationship.trim(),
        phone_number: otherIncomeForm.phone_number.trim(),
        address: otherIncomeForm.address.trim(),
        bank_reference: otherIncomeForm.bank_reference.trim(),
        bank_phone_number: otherIncomeForm.bank_phone_number.trim(),
        bank_account_type: otherIncomeForm.bank_account_type,
      };
    } else {
      const err = validateCreditForm();
      if (err) {
        toast.error(err);
        return;
      }
      payload = buildCreditPayload();
    }

    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/customer-info/request/${token}`, payload);
      setSubmitted(true);
      toast.success('Your information has been submitted. Thank you!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit information');
    } finally {
      setSubmitting(false);
    }
  };

  const pageTitle = requestMeta?.info_type === 'credit_app_supplement'
    ? 'Credit Application Information'
    : requestMeta?.info_type === 'other_income'
      ? 'Other Income Information'
      : 'Driver License Information';

  const frequencyOptions = requestMeta?.other_income_frequency_options || DEFAULT_OTHER_INCOME_FREQUENCY_OPTIONS;

  const renderLandlordFields = () => (
    <div className="space-y-4 border border-white/10 p-4">
      <h3 className="text-sm font-['Unbounded'] text-white">{SECTION_TITLES.landlord}</h3>
      <div>
        <label className={labelClass}>Landlord Name *</label>
        <input type="text" value={creditForm.landlord_name} onChange={(e) => setCreditForm({ ...creditForm, landlord_name: e.target.value })} className={inputClass} required />
      </div>
      <div>
        <label className={labelClass}>Landlord Mobile Number *</label>
        <input type="tel" value={creditForm.landlord_mobile} onChange={(e) => setCreditForm({ ...creditForm, landlord_mobile: e.target.value })} className={inputClass} required />
      </div>
    </div>
  );

  const renderPreviousResidentialFields = () => (
    <div className="space-y-4 border border-white/10 p-4">
      <h3 className="text-sm font-['Unbounded'] text-white">{SECTION_TITLES.previous_residential}</h3>
      <div>
        <label className={labelClass}>Previous Street Address *</label>
        <input type="text" value={creditForm.previous_street_address} onChange={(e) => setCreditForm({ ...creditForm, previous_street_address: e.target.value })} className={inputClass} required />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Years Stayed *</label>
          <input type="number" min="0" value={creditForm.previous_residence_years} onChange={(e) => setCreditForm({ ...creditForm, previous_residence_years: e.target.value })} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>Months Stayed *</label>
          <input type="number" min="0" max="11" value={creditForm.previous_residence_months} onChange={(e) => setCreditForm({ ...creditForm, previous_residence_months: e.target.value })} className={inputClass} required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>City *</label>
          <input type="text" value={creditForm.previous_city} onChange={(e) => setCreditForm({ ...creditForm, previous_city: e.target.value })} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>State *</label>
          <select value={creditForm.previous_state} onChange={(e) => setCreditForm({ ...creditForm, previous_state: e.target.value })} className={`${inputClass} [&>option]:bg-gray-900`} required>
            <option value="">Select state...</option>
            {(requestMeta?.us_states || US_STATES).map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>ZIP *</label>
          <input type="text" value={creditForm.previous_zip} onChange={(e) => setCreditForm({ ...creditForm, previous_zip: e.target.value })} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>Monthly Rent/Mortgage *</label>
          <input type="number" min="0" value={creditForm.previous_rent_mortgage} onChange={(e) => setCreditForm({ ...creditForm, previous_rent_mortgage: e.target.value })} className={inputClass} required />
        </div>
      </div>
    </div>
  );

  const renderOtherIncomeFields = () => (
    <div className="space-y-4 border border-white/10 p-4">
      <h3 className="text-sm font-['Unbounded'] text-white">Other Income Details</h3>
      <div>
        <label className={labelClass}>Other Income Amount *</label>
        <input type="number" min="0" value={otherIncomeForm.other_income_amount} onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, other_income_amount: e.target.value })} className={inputClass} required />
      </div>
      <div>
        <label className={labelClass}>Other Income Frequency *</label>
        <select value={otherIncomeForm.other_income_frequency} onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, other_income_frequency: e.target.value })} className={`${inputClass} [&>option]:bg-gray-900`} required>
          <option value="">Select frequency...</option>
          {frequencyOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>
      <div>
        <label className={labelClass}>Source of Other Income *</label>
        <input type="text" value={otherIncomeForm.source_of_other_income} onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, source_of_other_income: e.target.value })} className={inputClass} required />
      </div>
      <div>
        <label className={labelClass}>Reference *</label>
        <input type="text" value={otherIncomeForm.reference} onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, reference: e.target.value })} className={inputClass} required />
      </div>
      <div>
        <label className={labelClass}>Relationship *</label>
        <input type="text" value={otherIncomeForm.relationship} onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, relationship: e.target.value })} className={inputClass} required />
      </div>
      <div>
        <label className={labelClass}>Phone Number *</label>
        <input type="tel" value={otherIncomeForm.phone_number} onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, phone_number: e.target.value })} className={inputClass} required />
      </div>
      <div>
        <label className={labelClass}>Address *</label>
        <input type="text" value={otherIncomeForm.address} onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, address: e.target.value })} className={inputClass} required />
      </div>
      <div>
        <label className={labelClass}>Bank Reference *</label>
        <input type="text" value={otherIncomeForm.bank_reference} onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, bank_reference: e.target.value })} className={inputClass} required />
      </div>
      <div>
        <label className={labelClass}>Bank Phone Number *</label>
        <input type="tel" value={otherIncomeForm.bank_phone_number} onChange={(e) => setOtherIncomeForm({ ...otherIncomeForm, bank_phone_number: e.target.value })} className={inputClass} required />
      </div>
      <div>
        <label className={labelClass}>Account Type *</label>
        <div className="flex gap-4 mt-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={otherIncomeForm.bank_account_type === 'checking'}
              onChange={() => setOtherIncomeForm({ ...otherIncomeForm, bank_account_type: 'checking' })}
              className="accent-blue-500"
            />
            <span className="text-sm font-['Manrope'] text-white/80">Checking</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={otherIncomeForm.bank_account_type === 'saving'}
              onChange={() => setOtherIncomeForm({ ...otherIncomeForm, bank_account_type: 'saving' })}
              className="accent-blue-500"
            />
            <span className="text-sm font-['Manrope'] text-white/80">Saving</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderPreviousEmploymentFields = () => (
    <div className="space-y-4 border border-white/10 p-4">
      <h3 className="text-sm font-['Unbounded'] text-white">{SECTION_TITLES.previous_employment}</h3>
      <div>
        <label className={labelClass}>Previous Employer *</label>
        <input type="text" value={creditForm.previous_employer} onChange={(e) => setCreditForm({ ...creditForm, previous_employer: e.target.value })} className={inputClass} required />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Employment Years *</label>
          <input type="number" min="0" value={creditForm.previous_job_years} onChange={(e) => setCreditForm({ ...creditForm, previous_job_years: e.target.value })} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>Employment Months *</label>
          <input type="number" min="0" max="11" value={creditForm.previous_job_months} onChange={(e) => setCreditForm({ ...creditForm, previous_job_months: e.target.value })} className={inputClass} required />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <p className="text-white/50 font-['Manrope']">Loading secure form...</p>
      </div>
    );
  }

  if (!requestMeta) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white/5 border border-white/10 p-8 text-center">
          <h1 className="text-lg font-['Unbounded'] text-white mb-2">Link Unavailable</h1>
          <p className="text-sm font-['Manrope'] text-white/50">This customer information request could not be found.</p>
        </div>
      </div>
    );
  }

  const requestedSections = requestMeta.requested_sections || [];

  return (
    <div className="min-h-screen bg-[#050505] py-10 px-4" data-testid="customer-info-request-page">
      <div className="max-w-lg mx-auto">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-['Unbounded'] font-bold text-white mb-1">Drive Now Autohaus</h1>
          <p className="text-sm font-['Manrope'] text-white/50">{pageTitle}</p>
        </div>

        <div className="bg-white/5 border border-white/10 p-6">
          {submitted ? (
            <div className="text-center py-6" data-testid="customer-info-submitted">
              <h2 className="text-lg font-['Unbounded'] text-emerald-400 mb-2">Thank You</h2>
              <p className="text-sm font-['Manrope'] text-white/70">
                Your information has been received for{' '}
                <span className="text-white font-medium">{requestMeta.customer_name}</span>.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm font-['Manrope'] text-white/70 mb-6">
                Hello <span className="text-white font-medium">{requestMeta.customer_name}</span>,
                please provide the requested information below.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-info-form">
                {requestMeta.info_type === 'driver_license' && (
                  <>
                    <div>
                      <label className={labelClass}>Driver License Number # *</label>
                      <input type="text" value={dlForm.driver_license_number} onChange={(e) => setDlForm({ ...dlForm, driver_license_number: e.target.value })} data-testid="dl-number-input" className={inputClass} required />
                    </div>
                    <div>
                      <label className={labelClass}>DL Issuing County *</label>
                      <input type="text" value={dlForm.dl_issuing_county} onChange={(e) => setDlForm({ ...dlForm, dl_issuing_county: e.target.value })} data-testid="dl-county-input" className={inputClass} required />
                    </div>
                    <div>
                      <label className={labelClass}>DL Issuing State *</label>
                      <select value={dlForm.dl_issuing_state} onChange={(e) => setDlForm({ ...dlForm, dl_issuing_state: e.target.value })} data-testid="dl-state-select" className={`${inputClass} [&>option]:bg-gray-900`} required>
                        <option value="">Select state...</option>
                        {(requestMeta.us_states || US_STATES).map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>DL Expiring Date *</label>
                      <input type="date" value={dlForm.dl_expiry_date} onChange={(e) => setDlForm({ ...dlForm, dl_expiry_date: e.target.value })} data-testid="dl-expiry-input" className={inputClass} required />
                    </div>
                    <div>
                      <label className={labelClass}>Date Of Birth *</label>
                      <input type="date" value={dlForm.date_of_birth} onChange={(e) => setDlForm({ ...dlForm, date_of_birth: e.target.value })} data-testid="dob-input" className={inputClass} required />
                    </div>
                  </>
                )}

                {requestMeta.info_type === 'credit_app_supplement' && (
                  <>
                    {requestedSections.includes('landlord') && renderLandlordFields()}
                    {requestedSections.includes('previous_residential') && renderPreviousResidentialFields()}
                    {requestedSections.includes('previous_employment') && renderPreviousEmploymentFields()}
                  </>
                )}

                {requestMeta.info_type === 'other_income' && renderOtherIncomeFields()}

                <button type="submit" disabled={submitting} data-testid="customer-info-submit-btn" className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                  {submitting ? 'Submitting...' : 'Submit Information'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}